<template>
	<div class="message-actions-wrapper" :style="messageActionStyles">
		<div
			class="reaction-extension"
			:class="{ expanded: show && !trimActionArea }"
		>
			<div
				class="message-actions"
				:class="{ 'with-reactions': showReactionTray }"
			>
				<div
					class="action-list item-list"
					v-if="shouldShowActions"
					v-on-click-outside="closeTray"
					ref="actions"
				>
					<Tippy
						v-if="hasLocation"
						:content="$t('chat_messageaction_location')"
						:options="{ ...tipOptions, offset: [0, 2] }"
					>
						<LocationMap
							:lat="message.location_lat"
							:long="message.location_long"
							:accuracy="message.location_accuracy"
							:appcheck-verified="message.appcheck_verified"
							:user-id="message.user_id"
							:created-at="message.created_at"
							:popoverProps="{ fixedRight: 16 }"
							@open="onLocationMapOpen"
							@close="onLocationMapClose"
							ref="locationMap"
						>
							<LocationDotIcon
								class="location"
								:class="{ active: showLocationMap }"
								type="regular"
								role="button"
								:aria-label="$t('chat_messageaction_location')"
							/>
						</LocationMap>
					</Tippy>
					<Tippy
						v-if="!hideCreateThread"
						:content="$t('chat_messageaction_createthread')"
						:options="tipOptions"
					>
						<ReelIcon
							class="create-thread"
							type="solid"
							role="button"
							:aria-label="$t('chat_messageaction_createthread')"
							@click="$emit('create-thread')"
						/>
					</Tippy>
					<Tippy
						v-if="canClosePoll"
						:content="$t('chat_messageaction_closepoll')"
						:options="tipOptions"
					>
						<SquarePollHorizontalIcon
							class="poll"
							role="button"
							type="regular"
							:aria-label="$t('chat_messageaction_closepoll')"
							@click="showClosePollConfirm = true"
						/>
					</Tippy>
					<Tippy
						v-if="!hideReply"
						:content="$t('chat_messageaction_reply')"
						:options="tipOptions"
					>
						<ReplyIcon
							class="reply"
							type="regular"
							role="button"
							:aria-label="$t('chat_messageaction_reply')"
							@click="$emit('reply')"
						/>
					</Tippy>
					<Tippy
						v-if="!hideForward"
						:content="$t('chat_messageaction_forward')"
						:options="tipOptions"
					>
						<ForwardIcon
							class="forward"
							role="button"
							:aria-label="$t('chat_messageaction_forward')"
							@click="openForwardCard"
						/>
					</Tippy>
					<Tippy
						v-if="!hideReact"
						:content="$t('chat_messageaction_reaction')"
						:options="tipOptions"
					>
						<FaceSmileIcon
							class="react"
							type="regular"
							role="button"
							:aria-label="$t('chat_messageaction_reaction')"
							@click="openTray()"
						/>
					</Tippy>
					<Tippy
						v-if="!hideEdit"
						:content="$t('chat_messageaction_edit')"
						:options="tipOptions"
					>
						<PencilIcon
							class="edit"
							type="regular"
							:aria-label="$t('chat_messageaction_edit')"
							@click="editMessage"
						/>
					</Tippy>
					<Tippy
						v-if="!hideSeenBy"
						:content="$t('chat_readreceipts_title')"
						:options="tipOptions"
					>
						<EyeIcon
							class="seen-by"
							type="regular"
							:aria-label="$t('chat_readreceipts_title')"
							@click="showReadReceiptsModal = true"
						/>
					</Tippy>
					<Tippy
						v-if="!hidePin"
						:content="
							isPinned
								? $t('chat_messageaction_unpin')
								: $t('chat_messageaction_pin')
						"
						:options="tipOptions"
					>
						<ThumbtackIcon
							v-if="!isPinned"
							class="pin-message"
							type="regular"
							:aria-label="'Pin Message'"
							@click="showPinModal = true"
						/>

						<ThumbtackSlashIcon
							v-if="isPinned"
							class="pin-message unpin"
							type="regular"
							:aria-label="'Pin Message'"
							@click="unpinMessage()"
						/>
					</Tippy>
					<Tippy
						v-if="!hideDelete"
						:content="$t('chat_messageaction_delete')"
						:options="tipOptions"
					>
						<TrashCanIcon
							class="delete"
							type="regular"
							role="button"
							:aria-label="$t('chat_messageaction_delete')"
							@click="
								message.isFailed ? deleteMessage() : (confirmDelete = true)
							"
						/>
					</Tippy>
				</div>
				<div
					v-if="showReactionTray"
					class="reaction-list item-list"
					ref="reactions"
				>
					<div class="emoji-container">
						<div
							class="reaction-item"
							v-for="{ id, label, emoji } in allReactions"
							:key="id"
							@click.prevent="$emit('react', emoji)"
							role="img"
							:aria-label="label"
						>
							<span class="emoji-img" v-text="emoji" />
							<div
								class="selected-dot"
								:class="{
									active:
										message.reactions &&
										message.reactions[emoji] &&
										message.reactions[emoji].me,
								}"
							/>
						</div>
					</div>

					<EmojiPicker
						class="emojis"
						@change="$emit('react', $event.emoji)"
						@open="openPicker"
						@close="closePicker"
					>
						<div class="reaction-item add-reaction">
							<PlusIcon class="plus-icon" type="regular" />
						</div>
					</EmojiPicker>
				</div>
			</div>
		</div>

		<DeleteModal
			v-if="confirmDelete"
			@close="closeDeleteModal"
			@delete="deleteMessage()"
			:bodyText="$t('chat_message_confirmdelete_title')"
			:title="$t('chat_message_confirmdelete_button')"
			:isProcessing="showDeleteSpinner"
		/>

		<ReadReceiptsModal
			v-if="showReadReceiptsModal"
			:receiptsUrl="`messages/${message.id}/receipts`"
			:countsUrl="`messages/${message.id}/receipts/counts`"
			:bubbleId="message.bubble_id"
			@cancel="showReadReceiptsModal = false"
		/>

		<PinModal
			v-if="showPinModal"
			:messageId="message.id"
			:chatId="message.bubble_id"
			@close="showPinModal = false"
		/>

		<StandardModal
			v-if="showClosePollConfirm"
			open
			:title="$t('chat_close_poll_confirmation_title')"
			:confirmText="$t('chat_close_poll_confirmation_confirm')"
			:confirmProps="{ loading: isClosingPoll }"
			@confirm="closePoll"
			@cancel="showClosePollConfirm = false"
			small
			ref="closePollModal"
		>
			<div class="close-poll-modal-wrapper">
				<SquarePollHorizontalIcon halo class="close-poll-icon" type="regular" />
				<div class="help">
					{{ $t('chat_close_poll_confirmation_description') }}
				</div>
			</div>
		</StandardModal>
	</div>
</template>

<script>
import { deleteMessage, getMyMembership, updateChat } from '@/store'
import {
	TrashCanIcon,
	ForwardIcon,
	FaceSmileIcon,
	PencilIcon,
	ReplyIcon,
	EyeIcon,
	ThumbtackIcon,
	ThumbtackSlashIcon,
	PlusIcon,
	SquarePollHorizontalIcon,
	LocationDotIcon,
	ReelIcon,
} from '@/icons'
import DeleteModal from '../DeleteModal'
import { post } from '@/helpers/api'
import { Tippy, StandardModal } from '@/components/common'
import ReadReceiptsModal from './ReadReceiptsModal'
import PinModal from './PinModal'
import { updateChat as apiUpdateChat } from '@/api/chat'
import EmojiPicker from '@/components/common/EmojiPicker'
import LocationMap from '@/components/common/LocationMap'
import {
	getQuickReactionEmojis,
	getRecentEmojis,
	isQuickReactionEmoji,
	QUICK_REACTION_EMOJIS_MAX,
} from '@/helpers/emojis'
import { updatePoll } from '@/api/poll'
import { getPoll, updatePoll as updatePollStore } from '@/store/messages'
import { getMyUserId } from '@/store'
import { orgFlag, LOCATION_TAGGING } from '@/helpers/flags'

const DEFAULT_STYLES = {
	'--actions-width': '200px',
	'--actions-max-width': '400px',
}

export default {
	name: 'MessageActions',
	props: {
		message: Object,
		show: { type: Boolean, default: null },
		hideReply: { type: Boolean, default: null },
		hideForward: { type: Boolean, default: null },
		hideReact: { type: Boolean, default: null },
		hideDelete: { type: Boolean, default: null },
		hideEdit: { type: Boolean, default: null },
		hidePin: { type: Boolean, default: null },
		hideSeenBy: { type: Boolean, default: null },
		hideCreateThread: { type: Boolean, default: null },
		isPinned: { type: Boolean, default: null },
		// actions tray has a larger hover area so they don't disappear right away
		// this prop eliminates that expanded hover area (currently only used when
		// the previous message is being edited)
		trimActionArea: { type: Boolean, default: null },
	},
	components: {
		TrashCanIcon,
		ForwardIcon,
		FaceSmileIcon,
		ReplyIcon,
		PencilIcon,
		DeleteModal,
		Tippy,
		ReadReceiptsModal,
		EyeIcon,
		ThumbtackIcon,
		ThumbtackSlashIcon,
		PinModal,
		EmojiPicker,
		PlusIcon,
		SquarePollHorizontalIcon,
		StandardModal,
		LocationDotIcon,
		LocationMap,
		ReelIcon,
	},
	data() {
		return {
			showDeleteSpinner: false,
			showReactionTray: false,
			quickReactionEmojis: getQuickReactionEmojis(),
			recentEmojis: getRecentEmojis(),
			confirmDelete: false,
			tipOptions: { animation: 'scale' },
			showReadReceiptsModal: false,
			showPinModal: false,
			showEmojiPicker: false,
			showLocationMap: false,
			messageActionStyles: DEFAULT_STYLES,
			showClosePollConfirm: false,
			isClosingPoll: false,
		}
	},
	emits: [
		'reply',
		'react',
		'hide',
		'edit',
		'create-thread',
		'emoji-picker-opened',
		'emoji-picker-closed',
		'location-map-opened',
		'location-map-closed',
	],
	mounted() {
		this.$onEventBus('recent-emojis-updated', () => {
			this.recentEmojis = getRecentEmojis()
		})
		this.$onEventBus('open-location-map', () => {
			setTimeout(() => {
				this.$refs.locationMap?.openMap()
			}, 100)
		})
		this.$onEventBus('close-location-map', () => {
			this.$refs.locationMap?.closeMap()
		})
	},
	computed: {
		shouldShowActions() {
			return (
				(this.message.id || this.message.isFailed) &&
				this.show &&
				!this.confirmDelete &&
				!this.showReactionTray
			)
		},
		allReactions() {
			const withoutQuickReactions = this.recentEmojis.filter(
				(e) => !isQuickReactionEmoji(e.emoji)
			)
			return getQuickReactionEmojis()
				.concat(withoutQuickReactions)
				.slice(0, QUICK_REACTION_EMOJIS_MAX)
		},
		canClosePoll() {
			const poll = getPoll(this.message.poll_id)
			if (!this.message.poll_id || poll?.closed) return false
			if (poll?.user_id === getMyUserId()) return true // creator can close
			const memb = getMyMembership()
			return memb?.role === 'owner' // chat owners can also close
		},
		hasLocation() {
			return !!(
				this.message.location_lat &&
				this.message.location_long &&
				orgFlag(LOCATION_TAGGING)
			)
		},
	},
	watch: {
		async show(show) {
			if (!show) this.closeTray()
			await this.$nextTick()
			this.updateMessageActionStyles()
		},
		async showReactionTray() {
			await this.$nextTick()
			this.updateMessageActionStyles()
		},
	},
	methods: {
		closeDeleteModal() {
			this.confirmDelete = false
			this.$emit('hide')
		},

		editMessage() {
			this.$emit('edit')
		},

		openTray() {
			this.showReactionTray = true
		},

		closeTray() {
			// this has to be a method because v-on-click-outside requires it
			this.showReactionTray = false
		},

		openForwardCard() {
			EventBus.$emit('forwardCardOpen', {
				open: true,
				message_id: this.message.id,
			})
		},

		async deleteMessage() {
			try {
				this.showDeleteSpinner = true
				if (this.message.id) {
					await post('/api/v1/message.delete', { message_id: this.message.id })
					deleteMessage(this.message.id)
				} else {
					deleteMessage(this.message.uuid)
				}
			} catch (error) {
				console.error(error)
				EventBus.$emit('showFailBanner', {
					error: 'Unable to delete message.  Try again.',
				})
			} finally {
				this.showDeleteSpinner = false
				this.confirmDelete = false
			}
		},

		async unpinMessage() {
			try {
				updateChat(this.message.bubble_id, { pinned_message_id: null })
				await apiUpdateChat(this.message.bubble_id, {
					pinned_message_id: null,
				})
			} catch (e) {
				updateChat(this.message.bubble_id, {
					pinned_message_id: this.message.id,
				})
				this.$alert.error(this.$t('chat_pin_message_unpin_failure_title'))
			}
		},

		async closePoll() {
			if (!this.canClosePoll) return
			this.isClosingPoll = true
			const { poll_id } = this.message
			try {
				await updatePoll(poll_id, { closed: true })
				this.showClosePollConfirm = false
			} catch (e) {
				console.error(e)
				this.$refs.closePollModal?.$alert?.error(e)
			} finally {
				this.isClosingPoll = false
			}
		},

		openPicker() {
			this.showEmojiPicker = true
			this.$emit('emoji-picker-opened')
		},
		closePicker() {
			this.showEmojiPicker = false
			this.$emit('emoji-picker-closed')
		},

		onLocationMapOpen() {
			this.showLocationMap = true
			this.$emit('location-map-opened')
		},
		onLocationMapClose() {
			this.showLocationMap = false
			this.$emit('location-map-closed')
		},

		updateMessageActionStyles() {
			const PADDING = 16
			const { actions, reactions } = this.$refs

			if (!this.show) {
				this.messageActionStyles = DEFAULT_STYLES
				return
			}

			const actionsWidth = reactions?.scrollWidth || actions?.scrollWidth
			this.messageActionStyles['--actions-width'] =
				actionsWidth + PADDING + 'px'

			// Get max width from parent container
			const layoutEl = document.querySelector('.message-wrapper')
			this.messageActionStyles['--actions-max-width'] = layoutEl
				? layoutEl.offsetWidth + 'px'
				: '100%'
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.message-actions-wrapper {
	--horizontal-buffer: 16px;
	position: absolute;
	top: 0;
	right: 0;
	z-index: 3;

	.reaction-extension {
		position: absolute;
		top: 16px;
		right: calc(var(--horizontal-buffer) * -1);
		&.expanded {
			top: -51px;
			bottom: 0;
			height: 68px;
			width: calc(
				var(--actions-width, 0) + var(--horizontal-buffer) +
					var(--horizontal-buffer)
			);
		}
	}

	.message-actions {
		position: absolute;
		bottom: 0;
		right: var(--horizontal-buffer);
		color: var(--neutral-600);
		background: var(--neutral-0);
		box-shadow: 0 0 13px 0 rgba(203, 203, 203, 0.5);
		border-radius: 7px;
		padding: 0 8px;
		max-width: var(--actions-max-width, 100%);
		width: var(--actions-width, 0);
		overflow: hidden;
		&.with-reactions {
			transition: width 0.15s ease-in-out;
		}
	}
	.item-list {
		@include flex;
		width: max-content;
		> * {
			@include flex;
			flex-shrink: 0;
		}
	}
	.action-list {
		.seen-by,
		.react,
		.edit,
		.delete,
		.poll,
		.pin-message,
		.location,
		.create-thread,
		.reply {
			cursor: pointer;
			color: var(--neutral-600);
			font-size: 36px;
			&:hover {
				color: var(--teal-300);
			}
			&.active {
				color: var(--teal-300);
			}
			&.reply {
				font-size: 40px;
			}
		}
		.create-thread {
			transform: rotate(30deg);
		}
		.location {
			font-size: 37px;
			padding-top: 13px;
		}
		:deep(svg) {
			padding: 8px;
			cursor: pointer;

			&:last-child {
				margin-right: 0;
			}
			&:focus {
				outline: none;
			}
			&.forward {
				width: 38px;
				height: 38px;
			}
			&.edit {
				width: 36px;
				height: 33px;
			}
			&.forward {
				g {
					stroke: var(--neutral-600);
				}
				&:hover {
					g {
						stroke: var(--teal-300);
					}
				}
			}
		}
		.pin-message.unpin {
			:deep(svg) {
				padding-left: 0;
				height: 36px;
				width: 36px;
			}
		}
	}

	.reaction-list {
		display: flex;
		flex-wrap: nowrap;
		overflow-x: auto;
		min-width: 0;
		width: auto;
		max-width: 100%;
		align-items: center;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}

		.emoji-container {
			display: flex;
			flex-wrap: nowrap;
			overflow-x: auto;
			min-width: max-content;
			width: auto;
			padding-right: 32px;
			-ms-overflow-style: none;
			scrollbar-width: none;

			&::-webkit-scrollbar {
				display: none;
			}
		}

		.emojis {
			position: absolute;
			right: 8px;
			top: 50%;
			transform: translateY(-50%);
			background: var(--neutral-0);
			height: 100%;

			&::after {
				content: '';
				position: absolute;
				top: 0;
				right: 28px;
				bottom: 0;
				width: 15px;
				background: linear-gradient(
					to right,
					transparent 0%,
					var(--neutral-0) 100%
				);
				pointer-events: none;
			}
		}

		.reaction-item {
			cursor: pointer;
			display: flex;
			flex-direction: column;
			align-items: center;
			font-size: 14px;
			line-height: 1.2;
			margin: 2px;
			padding: 5px;
			flex-shrink: 0;
			&:last-child {
				margin-right: 0;
			}
			&:not(.add-reaction):hover {
				background-color: var(--neutral-100);
				border-radius: 8px;
			}
		}
		.selected-dot {
			background: var(--blue-300);
			width: 4px;
			height: 4px;
			border-radius: 2px;
			overflow: hidden;
			margin-bottom: -5px;
			margin-top: 2px;
			opacity: 0;
			&.active {
				opacity: 1;
			}
		}
		.emoji-img {
			width: 1em;
			height: 1em;
			font-size: 24px;
		}
	}

	.add-reaction {
		background-color: var(--neutral-100);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		margin: auto;
	}

	.plus-icon {
		width: 14px;
		height: 14px;
		color: var(--neutral-600);
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

.close-poll-modal-wrapper {
	height: 100%;
	padding: 16px 60px;
	@include flex-column-center;
	text-align: center;
	justify-content: space-around;
	.close-poll-icon {
		font-size: 60px;
		color: var(--blue-300);
	}
}

@include overrideScheme('dark') {
	.message-actions-wrapper {
		.message-actions {
			background: var(--neutral-300);
			box-shadow: 0 0 13px 0 rgba(0, 0, 0, 0.5);

			.emojis {
				background: var(--neutral-300);

				&::after {
					background: linear-gradient(
						to right,
						transparent 0%,
						var(--neutral-300) 100%
					);
				}
			}

			.add-reaction {
				background-color: var(--neutral-0);
			}
		}
	}
}
</style>
